import type { TFunction } from 'i18next';
import type { ConversationSummary } from '../types';

/** Local midnight for the given instant. */
function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Monday 00:00 local for the ISO-style week containing `d`. */
function startOfWeekMondayContaining(d: Date): Date {
  const y = d.getFullYear();
  const m = d.getMonth();
  const dayOfMonth = d.getDate();
  const dayOfWeek = d.getDay();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(y, m, dayOfMonth - daysFromMonday);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export type TimeBucketKey = 'today' | 'yesterday' | 'this_week' | 'last_week' | 'older';

export function getTimeBucket(iso: string): TimeBucketKey {
  try {
    const date = new Date(iso);
    const now = new Date();
    const todayStart = startOfLocalDay(now);
    const chatDayStart = startOfLocalDay(date);

    if (chatDayStart.getTime() === todayStart.getTime()) return 'today';

    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    if (chatDayStart.getTime() === yesterdayStart.getTime()) return 'yesterday';

    const thisWeekStart = startOfWeekMondayContaining(now);
    const nextWeekStart = new Date(thisWeekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);

    if (date >= thisWeekStart && date < nextWeekStart) return 'this_week';

    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    if (date >= lastWeekStart && date < thisWeekStart) return 'last_week';

    return 'older';
  } catch {
    return 'older';
  }
}

export const TIME_BUCKET_ORDER: { key: TimeBucketKey }[] = [
  { key: 'today' },
  { key: 'yesterday' },
  { key: 'this_week' },
  { key: 'last_week' },
  /** Older than last calendar week — shown as “Last month” in UI (may include older chats). */
  { key: 'older' },
];

export interface GroupedChatHistorySection {
  bucketKey: TimeBucketKey;
  sectionLabel: string;
  items: ConversationSummary[];
}

/**
 * Groups conversations into time buckets (Today, Yesterday, …) with newest-first order inside each bucket.
 * Section labels are resolved through `t` (from `chat.historyBuckets.*`) so the consuming component owns translation.
 */
export function groupConversationsByRecency(
  conversations: ConversationSummary[],
  t: TFunction,
): GroupedChatHistorySection[] {
  const sorted = [...conversations].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );

  const byBucket = new Map<TimeBucketKey, ConversationSummary[]>();
  for (const { key } of TIME_BUCKET_ORDER) {
    byBucket.set(key, []);
  }
  for (const c of sorted) {
    const bucket = getTimeBucket(c.updated_at);
    byBucket.get(bucket)!.push(c);
  }

  const sections: GroupedChatHistorySection[] = [];
  for (const { key } of TIME_BUCKET_ORDER) {
    const items = byBucket.get(key)!;
    if (items.length > 0) {
      sections.push({ bucketKey: key, sectionLabel: t(`chat.historyBuckets.${key}`), items });
    }
  }
  return sections;
}
